import React, { useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Image } from "@/components/common/image";
import { ModalCloseButton } from "@/components/common/modal-close-button";
import { MoreIcon, RefreshIcon, TrashIcon } from "@/components/ui/icon";
import { useDownload } from "@/hooks/use-download";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { AgentShareModal } from "../agent-share-modal";
import { PublishIcon } from "@/components/agent-share-modal/assets";
import { TextButton } from "@/components/common/text-button";
import { IconButton } from "@/components/common/icon-button";
import { MoreActionItem, MoreActionPopover } from "@/components/common/more-action-popover";
import { Edit3, ChevronLeft, ChevronRight, DownloadIcon, RefreshCcw } from "lucide-react";
import { ConfirmModal } from "@/components/common/confirm-modal";
import { AiCreationTypeIcon } from "../agent-creation/assets/ai-creation-icon";
import { InfoCircleIcon } from "../agent-creation/assets/info-circle-icon";
import { GenerateActorButton, SaveToMaterialButton, UseReferenceButton } from "../agent-creation/message";
import dayjs from "dayjs";
import { cn } from "@/lib/utils";
import { DynamicPopover } from "../common/dynamic-popover";
import ImageStack from "../common/image-stack";
import { getModelNameByValue } from "../agent-creation/constants/ai-creation-model";

export interface AgentAiCreationImageModalProps {
  open: boolean;
  imageUrls: string[];
  text: string;
  aiCreationModel?: string;
  aspectRatio?: string;
  imageSize?: string;
  model?: string;
  deleteLoading?: boolean;
  isAutoPush?: boolean;
  inputAddress?: string;
  currentIndex?: number;

  onOpenChange: (open: boolean) => void;
  onDownload?: (videoUrl: string) => void;
  onShare?: (videoUrl: string) => void;
  onReedit?: () => void;
  onDelete?: () => Promise<void>;
  onRegenerate?: () => void;
  onUseReferenceImage?: (url: string) => void;
}

export const AgentAiCreationImageModal: React.FC<AgentAiCreationImageModalProps> = ({
  open,
  onOpenChange,
  imageUrls,
  inputAddress,
  model,
  aspectRatio,
  imageSize,
  currentIndex: externalIndex,
  text,
  deleteLoading,
  isAutoPush,
  onDownload,
  onShare,
  onReedit,
  onDelete,
  onRegenerate,
  onUseReferenceImage,
}) => {
  const { t } = useTranslation();
  const { download } = useDownload();
  const { toast } = useToast();

  // 判断是否为横屏比例
  const isLandscapeRatio = (ratio?: string) => {
    if (!ratio) return false;
    const normalized = ratio.replace(':', '/');
    return ['4/3', '16/9', '21/9'].includes(normalized);
  };

  // 响应式宽度状态
  const [leftWidth, setLeftWidth] = useState<number>(498);
  const [windowWidth, setWindowWidth] = useState<number>(0);

  // 监听窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // 初始设置
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 根据窗口宽度和比例计算左侧宽度
  useEffect(() => {
    const isLandscape = isLandscapeRatio(aspectRatio);
    let baseWidth = isLandscape ? 610 : 498;

    if (windowWidth >= 2100) {
      baseWidth = isLandscape ? 960 : 667;
    }

    setLeftWidth(baseWidth);
  }, [windowWidth, aspectRatio]);

  // 当前显示的图片索引
  const [currentIndex, setCurrentIndex] = useState(externalIndex || 0);

  useEffect(() => {
    setCurrentIndex(externalIndex || 0);
  }, [externalIndex]);

  // 切换上一张（循环）
  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + imageUrls.length) % imageUrls.length);
  };

  // 切换下一张（循环）
  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % imageUrls.length);
  };

  const handleDownload = async (url: string) => {
    try {
      if (onDownload) {
        await Promise.resolve(onDownload(url));
        toast.default({ description: t('agent_creation.agent_modal.download_complete') });
        return;
      }
      await download(url, "image");
      toast.default({ description: t('agent_creation.agent_modal.download_complete') });
    } catch {
      toast.default({ description: t('agent_creation.agent_modal.download_fail') });
    }
  };
  const getAspectStyle = () => {
    if (!aspectRatio) return { aspectRatio: '9/16', maxWidth: `${leftWidth}px` }; // 默认比例
    return { aspectRatio: aspectRatio?.replace(':', '/'), maxWidth: `${leftWidth}px` };
  };
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-[24px] shadow-lg z-50 flex h-[90vh]"
        >
          {/* 左侧：图片区，带切换按钮 */}
          <div
            className={cn(
              "relative group bg-black flex items-center justify-center flex-shrink-0 flex-grow-0 h-[90vh] overflow-hidden",
            )}
            style={getAspectStyle()}
          >
            {/* 当前显示的图片 */}
            <Image
              src={imageUrls[currentIndex]}
              className={cn(
                "w-full h-full object-contain",
                aspectRatio === '9:16' && 'object-cover'
              )}
              alt="AI Generated Content"
            />

            {/* 左侧切换按钮 */}
            {imageUrls.length > 1 && (
              <IconButton
                className="size-9 rounded-[8px] absolute left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all bg-black/40 hover:bg-black/60 z-10"
                icon={<ChevronLeft className="w-5 h-5 text-white" />}
                onClick={handlePrev}
              />
            )}

            {/* 右侧切换按钮 */}
            {imageUrls.length > 1 && (
              <IconButton
                className="size-9 rounded-[8px] absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all bg-black/40 hover:bg-black/60 z-10"
                icon={<ChevronRight className="w-5 h-5 text-white" />}
                onClick={handleNext}
              />
            )}

            {/* 下载单张按钮 - 增加毛玻璃效果提升质感 */}
            <div
              className="py-1.5 px-3 bg-black/40 backdrop-blur-md rounded-[8px] absolute bottom-5 right-5 opacity-0 group-hover:opacity-100 transition-all flex items-center gap-x-2 hover:bg-black/60 cursor-pointer select-none z-10"
              onClick={() => handleDownload(imageUrls[currentIndex])}
            >
              <DownloadIcon className="w-4 h-4 text-white" />
              <span className="text-white text-xs font-medium">下载图片</span>
            </div>
          </div>

          {/* 右侧信息面板 */}
          <div className="p-6 w-[430px] h-full bg-white text-[rgba(56,65,74,1)] flex flex-col shrink-0 grow-0 gap-4 overflow-hidden">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2">
                <AiCreationTypeIcon className="size-6" />
                <span className="text-base font-medium text-[rgba(16,24,40,1)]">AI创作</span>
              </div>
              <ModalCloseButton
                onClick={() => onOpenChange(false)}
              />
            </div>

            {/* 右侧缩略图列表：四列，点击切换，当前项加边框高亮 */}
            <div className="grid grid-cols-4 gap-2">
              {imageUrls.map((url, index) => (
                <div
                  key={index}
                  className={cn(
                    "size-[90px] cursor-pointer rounded-[8px]",
                    "p-0.5",
                    index === currentIndex && 'outline outline-[1.5px] outline-i-normal rounded-[8px]'
                  )}
                  onClick={() => setCurrentIndex(index)}
                >
                  <Image
                    src={url}
                    alt={`缩略图-${index + 1}`}
                    className="w-full h-full rounded-[8px] object-cover"
                  />
                </div>
              ))}
            </div>

            {/* 内容区域*/}
            <div className="flex flex-col gap-2 flex-1 min-h-0 overflow-x-hidden overflow-y-auto">
              <label className="text-sm text-[rgba(102,112,133,1)]">脚本</label>
              <div className="text-sm text-[rgba(51,51,51,1)] ">
                {text}
              </div>

              <div className="flex items-center">
                {inputAddress && <>
                  <ImageStack images={inputAddress.split(",")} />
                  <span className="text-sm text-[rgba(102,112,133,1)] ml-2">参考图</span>
                  <span className='w-[1px] h-[14px] bg-[rgba(208,213,221,1)] mx-[9.5px]' />
                </>}
                <div className='flex items-center'>
                  {model && <span className='text-sm text-[rgba(102,112,133,1)]'>{getModelNameByValue(model)}</span>}
                  {aspectRatio && <span className='w-[1px] h-[14px] bg-[rgba(208,213,221,1)] mx-[9.5px]' />}
                  {aspectRatio && <span className='text-sm text-[rgba(102,112,133,1)]'>{aspectRatio}</span>}
                  <span className='w-[1px] h-[14px] bg-[rgba(208,213,221,1)] mx-[9.5px]' />
                  <DynamicPopover
                    side="bottom"
                    align="center"
                    mode="hover"
                    triggerHidden={false}
                    className="rounded-[16px] bg-[rgba(255,255,255,1)] [box-shadow:0px_0px_12px_rgba(16,_24,_40,_0.08)] p-4 border-none"
                    trigger={
                      <div className="cursor-pointer">
                        <InfoCircleIcon className="align-middle text-[rgba(102,112,133,1)] hover:text-[rgba(51,51,51,1)] transition-colors" />
                      </div>
                    }
                  >
                    <div className="flex items-center gap-2 ">
                      <span className="text-sm text-[rgba(102,112,133,1)]">生成时间</span>
                      <span className="text-sm text-[rgba(51,51,51,1)]">{dayjs(new Date()).format('YYYY-MM-DD HH:mm')}</span>
                    </div>
                  </DynamicPopover>
                </div>
              </div>
            </div>
            {/* 操作按钮区域 */}
            <div className="flex w-full gap-3 justify-end items-end flex-shrink-0 mt-auto">
              <MoreActionPopover
                className="w-40"
                side="top"
                align="center"
                tiggerClassName="size-9 rounded-[8px]"
                tiggerIcon={
                  <IconButton
                    icon={<MoreIcon className="size-5 !text-[rgba(51,51,51,1)]" />}
                  />
                }
              >
                <MoreActionItem
                  icon={<Edit3 className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                  className='text-[rgba(16,24,40,1)]'
                  label={t("agent_creation.content.reedit")}
                  onClick={() => {
                    onReedit?.();
                    onOpenChange(false);
                  }}
                />
                <MoreActionItem
                  icon={<RefreshCcw className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                  className='text-[rgba(16,24,40,1)]'
                  label={t("agent_creation.content.regenerate_again")}
                  onClick={() => {
                    onRegenerate?.();
                    onOpenChange(false);
                  }}
                />
                <MoreActionItem
                  icon={<DownloadIcon className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                  className='text-[rgba(16,24,40,1)]'
                  label={t("common.download")}
                  onClick={() => {
                    handleDownload(imageUrls[currentIndex]);
                  }}
                />
                <ConfirmModal
                  trigger={
                    <MoreActionItem
                      icon={<TrashIcon className="w-4 h-4 text-[rgba(51,51,51,1)]" />}
                      className='text-[rgba(16,24,40,1)]'
                      label={t("common.delete")}
                    />
                  }
                  title={t('agent_creation.content.delete_title')}
                  description={t('agent_creation.content.delete_desc')}
                  cancel={t('common.cancel')}
                  confirm={t('common.delete')}
                  onConfirm={async () => {
                    await onDelete?.();
                    onOpenChange(false);
                  }}
                  loading={deleteLoading}
                />
              </MoreActionPopover>
              <SaveToMaterialButton
                className="size-9 rounded-[8px]"
                iconClassName="size-5"
                url={imageUrls[currentIndex]}
              />
              <GenerateActorButton
                type="image"
                className="size-9 rounded-[8px]"
                iconClassName="size-5"
                fileUrl={imageUrls[currentIndex]}
              />
              <UseReferenceButton
                className="size-9 rounded-[8px]"
                iconClassName="size-5"
                onClick={() => onUseReferenceImage(imageUrls[currentIndex])}
              />
              <AgentShareModal
                showDouyin
                douyinOptions={{
                  imageUrls,
                }}
                trigger={
                  <TextButton
                    className="px-[22px] py-[10px] rounded-[8px] max-w-max text-sm"
                    icon={<PublishIcon className="w-5 h-5" />}
                    label={t('agent_creation.agent_modal.publish')}
                  />
                }
              />
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AgentAiCreationImageModal;
